import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HomeIcon from "@mui/icons-material/Home";
import PlaceIcon from "@mui/icons-material/Place";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ApiServices from "../../../Services/ApiServices/ApiServices";
import { useParams } from "react-router-dom";

const AUTO_SAVE_DELAY = 1200;

// Identify input type
const detectInputType = (text) => {
  const t = text.toLowerCase();

  if (t.includes("yes/no")) return "yes_no";
  if (t.includes("(yyyy-mm-dd)")) return "date";
  if (
    t.includes("how many") ||
    t.includes("number") ||
    t.includes("sq ft") ||
    t.includes("sqft")
  )
    return "number";
  if (t.includes("type") || (t.includes("(") && t.includes("/"))) return "select";

  return "text";
};

// Extract dropdown options
const extractSelectOptions = (text) => {
  const match = text.match(/\((.*?)\)/);
  if (!match) return [];
  return match[1].split("/").map((o) => o.trim());
};

const PropertyDetails = () => {
  const { id: propertyId } = useParams();

  const [property, setProperty] = useState(null);
  const [completion, setCompletion] = useState("0%");
  const [sections, setSections] = useState([]);

  const [answers, setAnswers] = useState({});
  const [dirtyAnswers, setDirtyAnswers] = useState({}); // 🔥 Track only changes

  const [loading, setLoading] = useState(true);

  const [saveStatus, setSaveStatus] = useState("saved"); // saving | saved | error
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  let saveTimeout = null;

  // Load questions + property data
  const loadData = async () => {
    try {
      const res = await ApiServices.getwtservices(
        `enquiry/property-all-questions/?property_id=${propertyId}`
      );

      setProperty(res.data.property);
      setCompletion(res.data.questionnaire_completion);
      setSections(res.data.sections);

      // Populate initial answers
      const initial = {};
      res.data.sections.forEach((section) =>
        section.questions.forEach((q) => {
          initial[q.id] = q.answer ?? ""; // backend may return null
        })
      );

      setAnswers(initial);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setSnack({ open: true, severity: "error", msg: "Failed to load questionnaire." });
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Handle user typing
  const handleChange = (qId, value) => {
    setAnswers((prev) => ({ ...prev, [qId]: value }));

    // Mark as dirty only if value is changed
    setDirtyAnswers((prev) => ({
      ...prev,
      [qId]: value,
    }));

    triggerAutoSave();
  };

  // Auto-save with delay
  const triggerAutoSave = useCallback(() => {
    setSaveStatus("saving");

    if (saveTimeout) clearTimeout(saveTimeout);
    saveTimeout = setTimeout(() => {
      saveData();
    }, AUTO_SAVE_DELAY);
  }, []);

  // Save changed answers only
  const saveData = async () => {
    const changedArray = Object.entries(dirtyAnswers).map(([qId, answer]) => ({
      question: Number(qId),
      answer,
    }));

    // If NOTHING changed → do not push empty payload
    if (changedArray.length === 0) {
      setSaveStatus("saved");
      return;
    }

    const payload = {
      property_id: propertyId,
      answers: changedArray, // only changed ones
    };

    try {
      await ApiServices.postwtservices("/enquiry/property-answer/", payload);

      setSaveStatus("saved");
      setDirtyAnswers({}); // clear tracked changes
      loadData(); // refresh progress %
    } catch (err) {
      console.error(err);
      setSaveStatus("error");
      setSnack({ open: true, severity: "error", msg: "Failed to save changes." });
    }
  };

  if (loading || !property)
    return (
      <Box p={5} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );

  const completionValue = parseFloat(completion);

  return (
    <Box p={3} maxWidth="900px" mx="auto">
      {/* ================= PROPERTY HEADER ================= */}
      <Box
        sx={{
          p: 3,
          borderRadius: 3,
          bgcolor: "#f7f9fc",
          mb: 4,
          boxShadow: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={3}>
          {/* Circular Progress Ring */}
          <Box position="relative" display="inline-flex">
            <CircularProgress
              variant="determinate"
              value={completionValue}
              size={90}
              thickness={5}
              sx={{ color: completionValue === 100 ? "success.main" : "primary.main" }}
            />
            <Box
              top={0}
              left={0}
              right={0}
              bottom={0}
              position="absolute"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Typography fontWeight={700} variant="h6">
                {completionValue}%
              </Typography>
            </Box>
          </Box>

          {/* Property Info */}
          <Box flex={1}>
            <Typography variant="h5" fontWeight={700}>
              {property.address_line1}
            </Typography>

            {property.address_line2 && (
              <Typography fontSize={14}>{property.address_line2}</Typography>
            )}

            <Box display="flex" alignItems="center" gap={1} mt={1}>
              <PlaceIcon color="error" fontSize="small" />
              <Typography>
                {property.city}, {property.state} {property.zipcode}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
              {property.is_primary_residence && (
                <Chip icon={<HomeIcon />} label="Primary Residence" color="success" />
              )}

              <Chip
                icon={<CalendarMonthIcon />}
                label={`Move-in: ${property.move_in_date}`}
                color="primary"
                variant="outlined"
              />

              {property.move_out_date && (
                <Chip
                  icon={<CalendarMonthIcon />}
                  label={`Move-out: ${property.move_out_date}`}
                  color="warning"
                />
              )}
            </Stack>
          </Box>
        </Box>

        {/* Save Status */}
        <Box mt={2} display="flex" justifyContent="flex-end">
          {saveStatus === "saving" && <Chip label="Saving..." color="warning" size="small" />}
          {saveStatus === "saved" && <Chip label="Saved" color="success" size="small" />}
          {saveStatus === "error" && <Chip label="Failed to Save" color="error" size="small" />}
        </Box>
      </Box>

      {/* ============== SECTIONS ============== */}
      {sections.map((section) => (
        <Accordion key={section.id} sx={{ borderRadius: 2, mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" fontWeight={700}>
              {section.display_name}
            </Typography>
          </AccordionSummary>

          <AccordionDetails>
            <Stack spacing={3}>
              {section.questions.map((q) => {
                const type = detectInputType(q.text);

                // SELECT (Yes/No or custom options)
                if (type === "select" || type === "yes_no") {
                  const options =
                    type === "yes_no" ? ["Yes", "No"] : extractSelectOptions(q.text);

                  return (
                    <FormControl fullWidth key={q.id}>
                      <InputLabel>{q.text}</InputLabel>
                      <Select
                        value={answers[q.id] ?? ""}
                        label={q.text}
                        onChange={(e) => handleChange(q.id, e.target.value)}
                      >
                        <MenuItem value="">Select</MenuItem>
                        {options.map((opt, idx) => (
                          <MenuItem key={idx} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  );
                }

                // DATE
                if (type === "date") {
                  return (
                    <TextField
                      key={q.id}
                      fullWidth
                      type="date"
                      label={q.text}
                      InputLabelProps={{ shrink: true }}
                      value={answers[q.id] || ""}
                      onChange={(e) => handleChange(q.id, e.target.value)}
                    />
                  );
                }

                // NUMBER
                if (type === "number") {
                  return (
                    <TextField
                      key={q.id}
                      fullWidth
                      type="number"
                      label={q.text}
                      value={answers[q.id] || ""}
                      onChange={(e) => handleChange(q.id, e.target.value)}
                    />
                  );
                }

                // TEXT
                return (
                  <TextField
                    key={q.id}
                    fullWidth
                    label={q.text}
                    value={answers[q.id] || ""}
                    onChange={(e) => handleChange(q.id, e.target.value)}
                  />
                );
              })}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Manual Save Button */}
      <Box textAlign="right" mt={2}>
        <Button variant="contained" size="large" sx={{ px: 4 }} onClick={saveData}>
          Save All
        </Button>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.severity}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PropertyDetails;
